import { useEffect, useRef } from "react";
import { useDict } from "@/i18n/useDict";

// Structural per-plan metadata (constant across languages) — zipped by index
// with dict.pricing.plans.
const PLAN_META = [
  { badge: "🟢", popular: false },
  { badge: "🔥", popular: true },
  { badge: "🚀", popular: false },
  { badge: "🏢", popular: false },
];

const SparkleIcon = () => (
  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
  </svg>
);

const CheckIcon = () => (
  <svg className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PricingSection = () => {
  const { dict } = useDict();
  const p13n = dict.pricing;
  const ref = useRef<HTMLDivElement>(null);

  const plans = p13n.plans.map((plan, i) => ({
    ...plan,
    ...PLAN_META[i],
    isEnterprise: i === p13n.plans.length - 1,
  }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="preturi" className="py-20 md:py-32 px-6 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-primary/5 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-4">
          <span className="inline-block mb-4 text-xs font-bold tracking-widest uppercase text-primary bg-primary/10 px-4 py-1.5 rounded-full">
            {p13n.eyebrow}
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-foreground leading-tight">
            {p13n.heading}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-xl mx-auto">
            {p13n.subheading}
          </p>
          <span className="inline-block mt-4 text-sm font-semibold text-primary bg-primary/10 px-4 py-1.5 rounded-full border border-primary/20">
            {p13n.annualDiscount}
          </span>
        </div>

        <div
          ref={ref}
          className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 stagger-children items-start"
        >
          {plans.map((p) => (
            <div
              key={p.badgeLabel}
              className={`animate-on-scroll relative flex flex-col ${p.popular ? "pt-5" : ""}`}
            >
              {p.popular && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
                  <span className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-[11px] font-bold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                    {p13n.recommended}
                  </span>
                </div>
              )}

              <div
                className={`relative rounded-2xl flex flex-col flex-1 transition-transform duration-300 hover:-translate-y-1 ${
                  p.popular
                    ? "border-2 border-primary bg-card pricing-popular"
                    : "border border-border bg-card"
                }`}
                style={{ boxShadow: p.popular ? "var(--shadow-pricing)" : "var(--shadow-card)" }}
              >
                {/* Card header */}
                <div className="px-6 pt-8 pb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">{p.badge}</span>
                    <span
                      className={`text-xs font-extrabold tracking-widest uppercase px-2.5 py-0.5 rounded-md ${
                        p.popular ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {p.badgeLabel}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-foreground leading-snug">
                    „{p.tagline}"
                  </h3>

                  <div className="mt-4 flex items-end gap-1">
                    <span className="text-4xl font-extrabold text-foreground">{p.price}</span>
                    <span className="text-muted-foreground text-sm mb-1">{p13n.perMonth}</span>
                  </div>

                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">
                    👉 {p.description}
                  </p>
                </div>

                {/* Features */}
                <div className="px-6 pb-6 flex-1 flex flex-col">
                  {p.baseLabel && (
                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">
                      {p.baseLabel}
                    </p>
                  )}
                  <ul className="flex-1 space-y-2 mb-5">
                    {p.features.map((f) => {
                      const isAI = !!f.ai;
                      const text = f.text;
                      return isAI ? (
                        <li key={text} className="flex items-start gap-2">
                          <div
                            className="flex items-start gap-2 w-full rounded-lg px-2.5 py-1.5 -mx-2.5"
                            style={{
                              background: "linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(99,102,241,0.08) 100%)",
                              border: "1px solid rgba(139,92,246,0.18)",
                            }}
                          >
                            <span style={{ color: "#8b5cf6" }} className="mt-0.5">
                              <SparkleIcon />
                            </span>
                            <span
                              className="text-sm font-medium"
                              style={{ color: "#7c3aed" }}
                            >
                              {text}
                            </span>
                          </div>
                        </li>
                      ) : (
                        <li key={text} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckIcon />
                          <span>{text}</span>
                        </li>
                      );
                    })}
                  </ul>

                  {p.result && (
                    <div
                      className={`rounded-xl px-4 py-3 mb-5 text-xs font-semibold leading-relaxed ${
                        p.popular
                          ? "bg-primary/10 text-primary border border-primary/20"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      💥 <span className="font-bold">{p13n.resultLabel}</span> {p.result}
                    </div>
                  )}

                  <a
                    href="#contact"
                    className={`inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold transition-all ${
                      p.popular
                        ? "bg-primary text-primary-foreground hover:opacity-90 shadow-md"
                        : "border-2 border-primary text-primary hover:bg-primary/10"
                    }`}
                  >
                    {p.isEnterprise ? p13n.ctaContact : p13n.ctaDemo}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground">
          {p13n.footnote}
        </p>
        <p className="mt-10 text-center text-xs text-muted-foreground">
          {p13n.footnote2}
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
